import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import { IconBulb } from '@tabler/icons-react';

import { Typography } from '@zendesk/forethought-elements';
import type { FontVariant } from './fontVariant';
import { getRecommendedFix } from './utils';
import { ErrorQueryEvent } from 'src/types/ErrorEventTypes';

export type RecommendedFixMode = 'compact' | 'default';

/**
 * `card` is the white bordered box the Error Logs pages use. `console` sits on
 * the preview console's dark background, whose text is white: the fix text
 * inherits that colour, so it needs a surface it can be read on.
 */
export type RecommendedFixSurface = 'card' | 'console';

const HEADER_VARIANT: Record<RecommendedFixMode, FontVariant> = {
  compact: 'font12Medium',
  default: 'font14Medium',
};

const BODY_VARIANT: Record<RecommendedFixMode, FontVariant> = {
  compact: 'font12',
  default: 'font16',
};

export const RecommendedFix = ({
  error,
  mode = 'default',
  surface = 'card',
}: {
  error: ErrorQueryEvent;
  mode?: RecommendedFixMode;
  surface?: RecommendedFixSurface;
}) => {
  const { t } = useTranslation('events');
  const { palette } = useTheme();

  const CurrentErrorFix = getRecommendedFix(error, BODY_VARIANT[mode]);

  return (
    CurrentErrorFix && (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: '6px' }}>
          <IconBulb
            color={
              surface === 'console'
                ? palette.colors.grey[500]
                : palette.colors.grey[700]
            }
            size={16}
          />
          <Typography variant={HEADER_VARIANT[mode]}>
            {t('recommendedFix.howToFix')}
          </Typography>
        </Box>
        <Box
          sx={{
            bgcolor:
              surface === 'console' ? 'transparent' : palette.colors.white,
            border: `1px solid ${
              surface === 'console'
                ? palette.colors.grey[700]
                : palette.colors.slate[200]
            }`,
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '16px',
          }}
        >
          {CurrentErrorFix}
        </Box>
      </Box>
    )
  );
};
